import { firstValidString, flagInSet } from '../../webmodule-common/other/ui/string-helper-functions';
import {
  FrameAttribute,
  FrameAttributeEditorType,
  FrameAttributeGroup,
  FrameAttributeGroupType,
  FrameBuyInItem,
  FrameV2,
  FrameValidationType
} from '../../webmodule-common/interop/webmodule-interop';
import { html, TemplateResult } from 'lit';
import { interpolateExternal } from '../../localization/global';
import { supplierQuoteItemContentType } from '../quotes/data/supplier-quote-item-content-type';

const typeMap: Record<string, string> = {
  [supplierQuoteItemContentType.CID_FRAM]: 'FRAM',
  [supplierQuoteItemContentType.CID_COMP]: 'COMP',
  [supplierQuoteItemContentType.CID_EXTN]: 'EXTN',
  [supplierQuoteItemContentType.CID_FILL]: 'FILL',
  [supplierQuoteItemContentType.CID_IGU]: 'IGU',
  [supplierQuoteItemContentType.CID_FREEHAND]: 'FREEHAND',
  [supplierQuoteItemContentType.CID_LOUVRE]: 'LOUVRE'
};

export function getBuyInResourceReference(frameBuyInItem: FrameBuyInItem) {
  const type = typeMap[frameBuyInItem.resourceType] ?? 'UNK';
  return `[${type}:${frameBuyInItem.extraDetails?.['SuppCode'] ?? ''}:${frameBuyInItem.code}]`;
  // Example: [FRAM:SUP123:CODE456], [IGU::CODE789]
}

export function getBuyInDetailsTemplate(frameBuyInItem: FrameBuyInItem, heightFirst: boolean | null) {
  const details = frameBuyInItem.extraDetails;
  const empty = ``;

  if (!details) return empty;

  switch (frameBuyInItem.resourceType) {
    case supplierQuoteItemContentType.CID_IGU:
    case supplierQuoteItemContentType.CID_FILL:
      return heightFirst
        ? `${details['HeightDisplayValue'] ?? empty} (H) x ${details['WidthDisplayValue'] ?? empty} (W)`
        : `${details['WidthDisplayValue'] ?? empty} (W) x ${details['HeightDisplayValue'] ?? empty} (H)`;
    case supplierQuoteItemContentType.CID_EXTN:
      return `${details['LengthDisplayValue'] ?? empty}`;
    default:
      return `Unknown Type`;
  }
}

export function getFrameDetailsTemplate(frame: FrameV2, isReports = true): TemplateResult {
  const attributeTemplate = (a: FrameAttribute) => {
    const visible = (isReports && !a.hideFromReport) || (!isReports && !a.hideFromUI);
    const displayValue = firstValidString(
      flagInSet(a.dataType, FrameAttributeEditorType.Dropdown || FrameAttributeEditorType.Picker)
        ? interpolateExternal(a.displayValue)
        : a.displayValue,
      a.value
    );
    return visible
      ? html` <li>
          <span class="attribute-label">${interpolateExternal(a.description)}</span>
          <span class="attribute-value">${displayValue}</span>
        </li>`
      : html``;
  };

  const groupHasAttributes = (g: FrameAttributeGroup) => {
    return g.attributes.filter(a => (isReports ? !a.hideFromReport : !a.hideFromUI)).length > 0;
  };
  const visibleGroups = (g: FrameAttributeGroup[]) => {
    return isReports
      ? g.filter(g => g.groupType !== FrameAttributeGroupType.Quote && groupHasAttributes(g) && !g.hideFromReport)
      : g.filter(g => g.groupType !== FrameAttributeGroupType.Quote && groupHasAttributes(g) && !g.hideFromUI);
  };
  const groupTemplate = (g: FrameAttributeGroup): TemplateResult => {
    const attributes = g.attributes.filter(a => (isReports && !a.hideFromReport) || (!isReports && !a.hideFromUI));
    if (attributes.length === 0) return html``;
    //we want to fit 3 columns per row??
    return html`
      <div class="col-4 extended-options-block">
        <h5>${interpolateExternal(g.description)}</h5>
        <ul>
          ${attributes.map(a => attributeTemplate(a))}
        </ul>
      </div>
    `;
  };

  const stackHasAttributes = (nf: FrameV2): boolean => {
    const processFrame = (fr: FrameV2): boolean => {
      if (fr.attributeGroups.some(g => groupHasAttributes(g))) return true;
      for (const face of fr.faces ?? []) {
        for (const light of face.lights ?? []) {
          for (const nested of light.nestedFrames) {
            if (processFrame(nested)) return true;
          }
        }
      }
      return false;
    };
    return processFrame(nf);
  };

  const frameDataTemplate = (frameData: FrameV2): TemplateResult => {
    const groups = visibleGroups(frameData.attributeGroups);
    const isMultiFace = (frameData.faces?.length ?? 0) > 1;

    const nestedFrameTemplate = () => {
      if (!frameData.faces || frameData.faces.length === 0) return html``;

      return html`
        <div class="extended-options-level1">
          ${frameData.faces.map(face => {
            const faceFrames = (face.lights ?? []).flatMap(l => l.nestedFrames).filter(nf => stackHasAttributes(nf));
            if (faceFrames.length === 0) return html``;

            // Multi-face: show face header. Single-face: skip face header.
            const faceHeader = isMultiFace ? html`<h3 class="extended-options-face-header">${face.name}</h3>` : html``;

            return html`
              ${faceHeader}
              ${faceFrames.map(
                f =>
                  html` <h4 class="extended-options-level-header">${interpolateExternal(f.description)}</h4>
                    <div class="extended-options-level2">${frameDataTemplate(f)}</div>`
              )}
            `;
          })}
        </div>
      `;
    };

    return html`
      <div class="row">${groups.map(g => groupTemplate(g))}</div>
      ${nestedFrameTemplate()}
    `;
  };

  return html`${frameDataTemplate(frame)}`;
}

/**
 * The frame-validation severity indicator: one icon per state, shared by every table that
 * renders quote-item tags (quote items, purchase-order items, fabrication-job items).
 *
 * Returns `null` rather than an empty template when the state carries no indicator, so a
 * caller can tell "no validation icon" from "icon rendered" and fall through to its own
 * tags (the quote table's no-tax marker, for instance).
 *
 * Kept here rather than inline in each table: the three copies had already drifted - `note`
 * rendered blue in one and primary in the other two (and in that one the colour came from a
 * page-stylesheet `text-*` class, which does not cross the table's shadow boundary, so the
 * icons were rendering uncoloured). The same copy had also lost the `outOfDate` case; that
 * table derives its state from the server's validation list and never runs a client-side
 * version check, so the gap was only reachable via a server-supplied `outOfDate` validation.
 */
export function frameValidationIcon(state: FrameValidationType): TemplateResult | null {
  switch (state) {
    case FrameValidationType.note:
      return html`<webmodule-icon library="fa" name="fas-file-lines" color="primary"></webmodule-icon>`;
    case FrameValidationType.information:
      return html`<webmodule-icon library="fa" name="fas-circle-info" color="cyan"></webmodule-icon>`;
    case FrameValidationType.warning:
      return html`<webmodule-icon library="fa" name="fas-triangle-exclamation" color="yellow"></webmodule-icon>`;
    case FrameValidationType.critical:
      return html`<webmodule-icon library="fa" name="fas-circle-exclamation" color="red"></webmodule-icon>`;
    case FrameValidationType.outOfDate:
      return html`<webmodule-icon library="fa" name="fas-wrench" color="red"></webmodule-icon>`;
  }
  return null;
}
